/**
 * ObjectiveCreateCustomPage — `/objectives/new/custom`
 *
 * Página dedicada para crear una campaña personalizada.
 * El form de 3 pasos (nombre + objetivo libre + métricas) tiene su propia
 * pantalla en vez de ser un bloque expandible — así no compite con el grid
 * de presets en /objectives/new.
 *
 * Al confirmar crea el draft con preset="custom" + customGoal +
 * customMetrics y navega al setup wizard.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    Loader2,
    AlertCircle,
    Wand2,
    CheckCircle2,
    Circle,
    Target as TargetIcon,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { useObjectives } from "../hooks/useObjectives";
import { TARGET_METRICS } from "../types";
import type { CampaignTargetMetric } from "../types";

const MOCK_BRAND_ID = "brand-demo";

export function ObjectiveCreateCustomPage() {
    const navigate = useNavigate();
    const { createDraft } = useObjectives(MOCK_BRAND_ID);

    const [name, setName] = useState("");
    const [goal, setGoal] = useState("");
    const [metricsSet, setMetricsSet] = useState<Set<CampaignTargetMetric>>(
        new Set(),
    );
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleMetric = (m: CampaignTargetMetric) => {
        setMetricsSet((prev) => {
            const next = new Set(prev);
            if (next.has(m)) next.delete(m);
            else next.add(m);
            return next;
        });
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError("Ponele un nombre a tu campaña");
            return;
        }
        if (!goal.trim()) {
            setError("Contanos qué querés lograr con esta campaña");
            return;
        }
        setSubmitting(true);
        setError(null);
        const draft = await createDraft({
            preset: "custom",
            name: name.trim(),
            customGoal: goal.trim(),
            customMetrics: Array.from(metricsSet),
        });
        setSubmitting(false);
        if (draft) {
            navigate(`/objectives/${draft.id}/setup`);
        } else {
            setError("No se pudo crear la campaña. Probá de nuevo.");
        }
    };

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-3xl space-y-6">
                {/* Breadcrumb */}
                <Link
                    to="/objectives/new"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Volver a presets
                </Link>

                {/* Hero — bloque grande arriba */}
                <div className="relative overflow-hidden rounded-2xl border-2 border-violet-300/60 bg-gradient-to-br from-white via-violet-50/40 to-fuchsia-50/40 p-6 md:p-8">
                    <div className="absolute -right-12 -top-12 w-64 h-64 bg-gradient-to-br from-violet-200/40 to-fuchsia-200/30 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative flex items-start gap-5">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-lg shadow-violet-500/30 flex-shrink-0">
                            <Wand2 size={28} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                                    Campaña personalizada
                                </span>
                                <span className="text-[10px] font-semibold text-slate-500">
                                    Sin plantilla — vos definís todo
                                </span>
                            </div>
                            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                                Armala desde cero
                            </h1>
                            <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-xl">
                                Si ningún preset encaja, contanos qué querés
                                hacer y la IA configura audiencia, plantilla y
                                cadencia para vos. Tres pasos rápidos.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form de 3 pasos */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8 space-y-7">
                    {/* 1. Nombre */}
                    <div>
                        <label className="block">
                            <span className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-md bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                                    1
                                </span>
                                Nombre de la campaña
                            </span>
                        </label>
                        <p className="text-[11px] text-slate-500 mt-1 ml-8">
                            Cómo la vas a reconocer en el listado.
                        </p>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value.slice(0, 80))}
                            placeholder="Reactivación Belgrano · Miércoles flojos"
                            maxLength={80}
                            className="mt-3 w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
                        />
                        <p className="text-[10px] text-slate-400 mt-1 text-right font-mono">
                            {name.length}/80
                        </p>
                    </div>

                    {/* 2. Objetivo (texto libre) */}
                    <div>
                        <label className="block">
                            <span className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-md bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                                    2
                                </span>
                                ¿Qué querés lograr con esta campaña?
                                <span className="text-[10px] text-slate-400 font-normal">
                                    (texto libre)
                                </span>
                            </span>
                        </label>
                        <p className="text-[11px] text-slate-500 mt-1 ml-8">
                            Cuanto más específico, mejor le va a sugerir audiencia
                            y mensaje la IA.
                        </p>
                        <textarea
                            value={goal}
                            onChange={(e) => setGoal(e.target.value.slice(0, 400))}
                            placeholder="Ej. Quiero contactar a clientes de la sucursal Belgrano que no pidieron en los últimos 45 días, con una promo de envío gratis los miércoles para llenar la cocina en un día flojo."
                            rows={4}
                            maxLength={400}
                            className="mt-3 w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all resize-none leading-relaxed"
                        />
                        <p className="text-[10px] text-slate-400 mt-1 text-right font-mono">
                            {goal.length}/400
                        </p>
                    </div>

                    {/* 3. Métricas */}
                    <div>
                        <label className="block">
                            <span className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-md bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center">
                                    3
                                </span>
                                ¿Qué métricas querés levantar?
                                <span className="text-[10px] text-slate-400 font-normal">
                                    (multi-select · opcional)
                                </span>
                            </span>
                        </label>
                        <p className="text-[11px] text-slate-500 mt-1 ml-8">
                            Marcá los KPIs que esta campaña debería mover. La IA
                            priorizará estos en su sugerencia y los va a monitorear
                            en el dashboard de la campaña.
                        </p>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            {TARGET_METRICS.map((m) => {
                                const isSelected = metricsSet.has(m.key);
                                return (
                                    <button
                                        key={m.key}
                                        type="button"
                                        onClick={() => toggleMetric(m.key)}
                                        className={`text-left p-3.5 rounded-xl border-2 transition-all flex items-start gap-3 ${
                                            isSelected
                                                ? "border-violet-500 bg-violet-50/50"
                                                : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                                        }`}
                                    >
                                        <span className="text-xl flex-shrink-0">
                                            {m.emoji}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p
                                                className={`text-sm font-semibold ${
                                                    isSelected
                                                        ? "text-violet-900"
                                                        : "text-slate-900"
                                                }`}
                                            >
                                                {m.label}
                                            </p>
                                            <p className="text-[11px] text-slate-500 mt-0.5">
                                                {m.description}
                                            </p>
                                        </div>
                                        {isSelected ? (
                                            <CheckCircle2
                                                size={18}
                                                className="text-violet-600 flex-shrink-0 mt-0.5"
                                            />
                                        ) : (
                                            <Circle
                                                size={18}
                                                className="text-slate-300 flex-shrink-0 mt-0.5"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-3">
                            {metricsSet.size === 0
                                ? "Ninguna seleccionada — opcional, pero ayuda a la IA"
                                : `${metricsSet.size} métrica${
                                      metricsSet.size > 1 ? "s" : ""
                                  } seleccionada${metricsSet.size > 1 ? "s" : ""}`}
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {/* Footer CTAs */}
                    <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100">
                        <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                            <TargetIcon size={11} />
                            Después configurás audiencia, plantilla y cadencia.
                        </p>
                        <div className="flex items-center gap-2">
                            <Link to="/objectives/new">
                                <Button variant="secondary" disabled={submitting}>
                                    Cancelar
                                </Button>
                            </Link>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={
                                    submitting || !name.trim() || !goal.trim()
                                }
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={15} className="animate-spin" />
                                        Creando…
                                    </>
                                ) : (
                                    <>
                                        <Wand2 size={15} />
                                        Crear campaña
                                        <ArrowRight size={15} />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ObjectiveCreateCustomPage;
